import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Drawer from 'react-bottom-drawer';
import chroma from 'chroma-js';
import { ReactComponent as WarningIcon } from '../../assets/icons/warning-triangle-transparent-exclamation-mark.svg';
import { ReactComponent as ClockIcon } from '../../assets/icons/clock.svg';
import { ReactComponent as PinIcon } from '../../assets/imgs/chats/pin.svg';
import { ReactComponent as EnvelopeIcon } from '../../assets/icons/envelope.svg';
import { palette } from '../../theme/colors';
import { translate } from '../../utils/translate';
import { getLegacyHumanReadableTimeDifference } from '../../utils/date';
import type { AlarmsAccountTitle } from './Alarm';
import { TitledAlarmTypeGroup } from '../../components/AlarmItem/TitledAlarmTypeGroup';

type BlockingAlarmItem = {
  alarmId: number;
  alarmSentAt: string | null | undefined;
  organizationName: string | null | undefined;
  name: string | null | undefined;
};

type Props = {
  alarms: BlockingAlarmItem[];
  primaryColor: string | null | undefined;
  canTriggerOtherAlarms: boolean;
  sheetSections: AlarmsAccountTitle[];
  currentLang: string;
};

export const OngoingBlockingAlarmNotice = ({
  alarms,
  primaryColor,
  canTriggerOtherAlarms,
  sheetSections,
  currentLang,
}: Props) => {
  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const theOnlyAlarm = alarms.length === 1 ? alarms[0] : undefined;
  const backgroundColor =
    primaryColor && chroma.valid(primaryColor) ? primaryColor : palette.danger;
  const ctaForeground = chroma(backgroundColor).darken(1.5).hex();

  const titleText = theOnlyAlarm
    ? translate('template_ongoing_alarm_warning_title', {
        alarm: theOnlyAlarm.name || translate('template_alarm_active_pill_blocking_alarm'),
        where: theOnlyAlarm.organizationName || translate('template_alarm_active_pill_account'),
      })
    : translate('template_ongoing_alarm_warning_title_multiple', {
        count: alarms.length,
      });

  const onOpenMessages = () => {
    if (theOnlyAlarm) {
      navigate(`/message/${theOnlyAlarm.alarmId}`);
    } else {
      navigate('/chat');
    }
  };

  return (
    <>
      <Container $backgroundColor={backgroundColor}>
        <Hero>
          <WarningIcon width={84} height={84} />
          <Title>{titleText}</Title>

          {theOnlyAlarm
            ? theOnlyAlarm.alarmSentAt != null && <AlarmTime sentAt={theOnlyAlarm.alarmSentAt} />
            : alarms.length > 0 && (
                <AlarmsList>
                  {alarms.map(alarm => (
                    <AlarmsListRow key={alarm.alarmId}>
                      <PinIcon width={20} height={20} />
                      <AlarmsListOrg>
                        {alarm.organizationName || translate('template_alarm_active_pill_account')}
                      </AlarmsListOrg>
                    </AlarmsListRow>
                  ))}
                </AlarmsList>
              )}
        </Hero>

        <Buttons>
          <CtaButton
            $ctaForeground={ctaForeground}
            onClick={onOpenMessages}
            data-e2e="blockingAlarm_openMessages_button"
          >
            <EnvelopeIcon width={20} height={20} />
            <span>{translate('template_alarm_open_messages')}</span>
          </CtaButton>

          {canTriggerOtherAlarms && (
            <SecondaryButton
              onClick={() => setIsDrawerOpen(true)}
              data-e2e="blockingAlarm_triggerOther_button"
            >
              {translate('template_alarm_trigger_other')}
            </SecondaryButton>
          )}
        </Buttons>
      </Container>

      <DrawerWrapper>
        <Drawer
          className="blockingAlarmDrawer"
          isVisible={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          hideScrollbars
        >
          <SheetList>
            {sheetSections.map(section => (
              <TitledAlarmTypeGroup
                key={`sheet-${section.id}`}
                titledAlarmsGroup={section}
                currentLang={currentLang}
              />
            ))}
          </SheetList>
        </Drawer>
      </DrawerWrapper>
    </>
  );
};

const AlarmTime = ({ sentAt }: { sentAt: string }) => (
  <TimeRow>
    <ClockIcon width={16} height={16} />
    <TimeText>{getLegacyHumanReadableTimeDifference(sentAt)}</TimeText>
  </TimeRow>
);

const Container = styled.div<{ $backgroundColor: string }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2.5rem;
  padding: 1.5rem 1.5rem 6rem;
  background-color: ${props => props.$backgroundColor};
  min-height: 100%;
`;

const Hero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  width: 100%;
`;

const Title = styled.p`
  font-family: 'Roboto-Bold';
  font-size: 1.625rem;
  line-height: 1.3;
  color: ${palette.white};
  text-align: center;
  margin: 0;
`;

const TimeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;

  svg path {
    fill: ${palette.white};
  }
`;

const TimeText = styled.span`
  font-family: 'Roboto-Regular';
  font-size: 0.9375rem;
  color: ${palette.white};
  text-transform: lowercase;
`;

const AlarmsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  width: 100%;
`;

const AlarmsListRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.375rem;

  svg path {
    fill: ${palette.white};
  }
`;

const AlarmsListOrg = styled.span`
  font-family: 'Roboto-Medium';
  font-size: 0.9375rem;
  color: ${palette.white};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
`;

const CtaButton = styled.button<{ $ctaForeground: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  width: 100%;
  padding: 1.125rem 1.5rem;
  border-radius: 999px;
  border: none;
  background-color: ${palette.white};
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }

  svg path {
    fill: ${props => props.$ctaForeground};
  }

  span {
    font-family: 'Roboto-Bold';
    font-size: 1rem;
    color: ${props => props.$ctaForeground};
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  padding: 1rem 1.5rem;
  border-radius: 999px;
  border: 1.5px solid rgba(255, 255, 255, 0.3);
  background: none;
  cursor: pointer;
  font-family: 'Roboto-Medium';
  font-size: 1rem;
  color: ${palette.white};
  text-align: center;

  &:hover {
    opacity: 0.9;
  }
`;

const DrawerWrapper = styled.div`
  .blockingAlarmDrawer {
    left: 0;
    right: 0;
    margin: auto;
    z-index: 1001;
    @media (min-width: 450px) {
      max-width: 26rem;
      margin: auto;
    }
    background-color: ${palette.prussianBlue2};
  }
  .blockingAlarmDrawer__backdrop {
    z-index: 1000;
  }
  .blockingAlarmDrawer__handle-wrapper {
    padding: 0;
  }
  .blockingAlarmDrawer__handle {
    width: 36%;
    margin-top: 1.3125rem;
  }
  .blockingAlarmDrawer__content {
    padding: 1rem 1.5rem;
    display: flex;
    flex-direction: column;
  }
`;

const SheetList = styled.div`
  height: 25rem;

  .item + * {
    margin-top: 10px;
  }

  overflow-y: auto;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;
